/**
 * Blockhyresavtal för företagsbostäder · Version 2026 (Formulär R-26).
 *
 * Sections are auto-numbered § 1–N in the legacy declared order; the
 * single-checkbox provisions self-hide and the rest renumber. The contract's
 * own custom sections (ADR-0060) are spliced in after the section they are
 * anchored to — one placement instance per declared section, gated on the
 * same visibility as its anchor so a custom section never outlives it.
 * Bilagor is the last numbered §; only Underskrift after it is unnumbered.
 */
import { COMPOSITION_SCHEMA, COMPOSITION_VERSION } from '@hyranai/regta-document-ir';
const TITLE = 'Blockhyresavtal för företagsbostäder · Version 2026';
const FORM_NUMBER = 'Formulär R-26';
const PREAMBLE = 'Undertecknande har denna dag träffat följande blockhyresavtal för företagsbostadsändamål.';
const LESSOR = { id: 'lessor', label: 'Hyresvärd', role: 'lessor', ordinal: 0, required: true, signs: true };
const LESSEE = {
    id: 'lessee',
    label: 'Blockhyresgäst',
    role: 'lessee',
    ordinal: 0,
    required: true,
    signs: true,
    kind: 'organisation',
};
/** Legacy `metadata.sections`, in order: ref, block, and its visibility. */
/** Sections laid beside the previous one, as in the legacy `grid-cols-2` rows (§ 1 | § 2, Hyrestid | Uppsägning). */
const BESIDE_PREVIOUS = new Set(['blockhyresgast', 'uppsagning']);
const SECTIONS = [
    { ref: 'hyresvard', blockRef: 'blockhyra.hyresvard@v1' },
    { ref: 'blockhyresgast', blockRef: 'blockhyra.blockhyresgast@v1' },
    { ref: 'upplatelseandamal', blockRef: 'blockhyra.upplatelseandamal@v1' },
    { ref: 'omfattning', blockRef: 'blockhyra.omfattning@v1' },
    { ref: 'skick', blockRef: 'blockhyra.skick@v1' },
    { ref: 'hyrestid', blockRef: 'blockhyra.hyrestid@v1' },
    { ref: 'uppsagning', blockRef: 'blockhyra.uppsagning@v1' },
    { ref: 'hyra', blockRef: 'blockhyra.hyra@v1' },
    { ref: 'indexklausul', blockRef: 'blockhyra.indexklausul@v1', visibleWhen: { key: 'index_clause_included', equals: true } },
    { ref: 'betalning', blockRef: 'blockhyra.betalning@v1' },
    { ref: 'ranta', blockRef: 'blockhyra.ranta@v1' },
    { ref: 'reservnycklar', blockRef: 'blockhyra.reservnycklar@v1' },
    { ref: 'underhall', blockRef: 'blockhyra.underhall@v1' },
    {
        ref: 'besittningsskydd',
        blockRef: 'blockhyra.besittningsskydd@v1',
        visibleWhen: { key: 'possession_protection_indirect', equals: true },
    },
    { ref: 'forutsattning', blockRef: 'blockhyra.forutsattning@v1', visibleWhen: { key: 'requires_tribunal_approval', equals: true } },
    { ref: 'ansokan', blockRef: 'blockhyra.ansokan@v1', visibleWhen: { key: 'tenant_applies_approval', equals: true } },
    { ref: 'sarskild-anpassning', blockRef: 'blockhyra.sarskild-anpassning@v1', visibleWhen: { key: 'special_adaptation', equals: true } },
    { ref: 'allmanna-villkor', blockRef: 'blockhyra.allmanna-villkor@v1' },
    { ref: 'inskrivning', blockRef: 'blockhyra.inskrivning@v1', visibleWhen: { key: 'no_inscription', equals: true } },
    { ref: 'personuppgifter', blockRef: 'blockhyra.personuppgifter@v1', visibleWhen: { key: 'pdpa_info_included', equals: true } },
    { ref: 'ovriga-bestammelser', blockRef: 'blockhyra.ovriga-bestammelser@v1' },
];
export const BLOCKHYRA_SECTION_REFS = SECTIONS.map((s) => s.ref);
export const blockhyraV1 = (values = {}) => ({
    schema: COMPOSITION_SCHEMA,
    version: COMPOSITION_VERSION,
    documentKey: 'blockhyra',
    documentVersion: 'v1',
    title: TITLE,
    locale: 'sv-SE',
    meta: {
        category: 'Blockhyra',
        numberStyle: 'paragraph',
        shortTitle: 'Blockhyresavtal',
        formNumber: FORM_NUMBER,
        preamble: PREAMBLE,
        statute: '12 kap. jordabalken',
        signingMode: 'digital',
        description: 'Blockhyresavtal för uthyrning av minst tre lägenheter (företagsbostäder) till en blockhyresgäst som i sin tur upplåter dem i andra hand — antingen till sina egna anställda eller en avgränsad krets (Punkt 3a), eller till en juridisk person som erbjuder bostad åt sin personal (Punkt 3b).\n\n' +
            'Hyran anges per år. Besittningsskyddet följer lokalhyresreglerna (indirekt) eller hyreslagens regler om direkt besittningsskydd. Avtalet kan kräva hyresnämndens tillstånd enligt 12 kap. 1 e § jordabalken.\n\n' +
            'Undertecknas av hyresvärd och blockhyresgäst.',
        partySlots: [LESSOR, LESSEE],
    },
    values,
    blocks: [
        { instanceId: 'letterhead', blockRef: 'common.letterhead@v1', slot: 'letterhead', input: { title: TITLE, form_number: FORM_NUMBER } },
        { instanceId: 'footer', blockRef: 'common.page-footer@v1', slot: 'footer', input: { form_number: FORM_NUMBER } },
        { instanceId: 'avtalsuppgifter', blockRef: 'blockhyra.avtalsuppgifter@v1', input: {} },
        { instanceId: 'preamble', blockRef: 'common.preamble@v1', input: { text: PREAMBLE, variant: 'intro' } },
        ...SECTIONS.flatMap((s) => [
            { instanceId: s.ref, blockRef: s.blockRef, input: BESIDE_PREVIOUS.has(s.ref) ? { beside_previous: true } : {} },
            {
                instanceId: `custom-after-${s.ref}`,
                blockRef: 'clean_bilaga.custom-sections@v1',
                input: { after_ref: s.ref, declared_refs: BLOCKHYRA_SECTION_REFS },
                ...(s.visibleWhen ? { includeWhen: s.visibleWhen } : {}),
            },
        ]),
        { instanceId: 'bilagor', blockRef: 'blockhyra.bilagor@v1', input: {} },
        {
            instanceId: 'underskrifter',
            blockRef: 'blockhyra.signing@v1',
            input: {
                parties: [
                    { label: 'Hyresvärd', role: 'lessor', ordinal: 0 },
                    { label: 'Hyresgäst', role: 'lessee', ordinal: 0 },
                ],
            },
        },
    ],
});
//# sourceMappingURL=blockhyra.js.map